import GuestReschedule from './pages/GuestReschedule';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getSession, onAuthStateChange, isAdmin } from './lib/auth';
import Header from './components/Header';
import Footer from './components/Footer';
import SupportFab from './components/SupportFab';
import Login from './components/Login';
import { ToastProvider, useToast } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import Home from './pages/Home';
import Settings from './pages/Settings';
import Events from './pages/Events';
import Members from './pages/Members';
import MemberDetails from './pages/MemberDetails';
import AdminPage from './pages/Admin.jsx';
import Prospect from './pages/Prospect';
import AcceptInvite from './pages/AcceptInvite';
import ResetPassword from './pages/ResetPassword';
import MeetingHost from './pages/MeetingHost';
import MeetingRegister from './pages/MeetingRegister';
import MeetingWelcome from './pages/MeetingWelcome';
import Executive from './pages/Executive';

function MeetingInvite() {
  return (
    <div className="max-w-3xl mx-auto py-10 px-6">
      <h1 className="text-2xl font-semibold text-NBNA-primary">Meeting Invite</h1>
      <p className="mt-2 text-gray-700">Please follow the instructions on this page to continue.</p>
    </div>
  );
}

function AppRoutes() {
  const { showToast } = useToast();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    // Check for existing session
    checkSession();

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setSession(session);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function checkSession() {
    try {
      const session = await getSession();
      setIsAuthenticated(!!session);
      setSession(session); // Make sure to set the session
    } catch (error) {
      console.error('Session check error:', error.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex flex-col overflow-hidden">
          <ErrorBoundary onError={() => showToast('Something went wrong', { kind: 'error' })}>
            <Routes>
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Login onLogin={() => setIsAuthenticated(true)} />
                  )
                }
              />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/members" element={<Members />} />
              <Route path="/members/:id" element={<MemberDetails />} />
              <Route path="/events" element={<Events />} />
              <Route
                path="/settings"
                element={
                  isAuthenticated ? <Settings userId={session?.user?.id} /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/admin"
                element={
                  isAuthenticated ? (
                    isAdmin(session) ? (
                      <AdminPage />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              <Route
                path="/executive"
                element={
                  isAuthenticated ? (
                    isAdmin(session) ||
                    session?.user?.backendRole === 'Executive' ||
                    session?.user?.role === 'Executive' ||
                    session?.user?.uiRole === 'executive' ? (
                      <Executive />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route path="/guest/reschedule" element={<GuestReschedule />} />
              <Route path="/meeting-invite" element={<MeetingInvite />} />
              <Route
                path="/meeting/host"
                element={isAuthenticated ? <MeetingHost /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/meeting/register"
                element={<MeetingRegister />}
              />
              <Route path="/meeting/welcome" element={<MeetingWelcome />} />
              <Route
                path="/prospect"
                element={
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <Prospect />
                  </div>
                }
              />

              <Route
                path="/"
                element={
                  <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <Home isAuthenticated={isAuthenticated} user={session?.user ?? null} />
                  </div>
                }
              />
            </Routes>
          </ErrorBoundary>
        </main>
        <Footer />
        {isAuthenticated && <SupportFab />}
      </div>
    </Router>
  );
}

function App() {
  return (
    <ToastProvider>
      <AppRoutes />
    </ToastProvider>
  );
}

export default App;
